import React, { useEffect } from 'react';
import { ArrowLeft, FileText, CreditCard, UserCheck, Copyright, AlertTriangle, Scale, Mail, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const terms = [
    {
      icon: CheckCircle,
      title: "Our Services",
      content: "We provide professional consultancy and ISO audits. These are advisory in nature; certification or compliance outcomes are not guaranteed.",
      color: "from-blue-500/20 to-turquoise-500/20"
    },
    {
      icon: CreditCard,
      title: "Payments & Refunds",
      content: "Fees are agreed upfront. Refunds, if any, are handled case by case.",
      color: "from-green-500/20 to-turquoise-500/20"
    },
    {
      icon: UserCheck,
      title: "Your Responsibilities",
      content: "Provide accurate information and comply with laws. Misuse of our site or services may result in access termination.",
      color: "from-purple-500/20 to-turquoise-500/20"
    },
    {
      icon: Copyright,
      title: "Our Content",
      content: "Text, graphics, and templates are owned by us. Do not copy or reuse without permission.",
      color: "from-orange-500/20 to-turquoise-500/20"
    },
    {
      icon: AlertTriangle,
      title: "Limits of Liability",
      content: "We provide guidance, but are not responsible for indirect losses, business outcomes, or missed opportunities.",
      color: "from-red-500/20 to-turquoise-500/20"
    },
    {
      icon: Scale,
      title: "Governing Law",
      content: "These terms are governed by the laws of India, with disputes resolved under the courts of Tamil Nadu.",
      color: "from-indigo-500/20 to-turquoise-500/20"
    }
  ];

  return (
    <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Back Button */}
        <Link to="/">
          <Button className="btn-secondary mb-8">
            <ArrowLeft size={20} />
            Back to Home
          </Button>
        </Link>

        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-turquoise-500/20 to-blue-500/20 rounded-full flex items-center justify-center border border-turquoise-500/30">
              <FileText size={40} className="text-turquoise-400" />
            </div>
          </div>
          <h1 className="display-large mb-6">
            Terms of <span className="turquoise-text-gradient">Service</span>
          </h1>
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Welcome to ShansoConsult! By using our website or consultancy services, you agree to the following terms:
          </p>
        </div>

        {/* Welcome Message */}
        <div className="bg-gradient-to-r from-turquoise-500/10 to-blue-500/10 border border-turquoise-500/20 rounded-16 p-8 mb-12">
          <div className="flex items-center gap-4 mb-4">
            <CheckCircle size={24} className="text-turquoise-400" />
            <h3 className="heading-3 text-white">Agreement Acknowledgment</h3>
          </div>
          <p className="body-medium text-gray-300">
            By accessing and using ShansoConsult's services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
        </div>

        {/* Terms Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {terms.map((term, index) => (
            <div key={index} className={`terms-card bg-gradient-to-br ${term.color} border border-turquoise-500/20 rounded-16 p-8 hover:border-turquoise-500/40 transition-all duration-300`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-turquoise-500/20 rounded-full flex items-center justify-center flex-shrink-0 border border-turquoise-500/30">
                  <term.icon size={24} className="text-turquoise-400" />
                </div>
                <div className="flex-1">
                  <h3 className="heading-3 mb-4 text-white">{term.title}</h3>
                  <p className="body-medium text-gray-300 leading-relaxed">{term.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-16 p-8 mb-12">
          <div className="flex items-center gap-4 mb-4">
            <AlertTriangle size={24} className="text-orange-400" />
            <h3 className="heading-3 text-white">Important Notice</h3>
          </div>
          <p className="body-medium text-gray-300">
            These terms constitute a legally binding agreement between you and ShansoConsult. Please review them carefully before using our services.
          </p>
        </div>

        {/* Contact Section */}
        <div className="text-center bg-gradient-to-br from-gray-900/50 to-turquoise-500/5 border border-turquoise-500/20 rounded-16 p-12">
          <div className="w-16 h-16 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-turquoise-500/30">
            <Mail size={32} className="text-turquoise-400" />
          </div>
          <h3 className="heading-2 mb-4 text-white">Questions About Terms?</h3>
          <p className="body-large text-gray-300 mb-6">
            Need clarification on any of these terms? We're here to help.
          </p>
          <Button 
            className="btn-primary-enhanced"
            onClick={() => window.open('mailto:shunmugavelsubramanian@gmail.com', '_blank')}
          >
            <Mail size={20} />
            Contact Us
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;